import { useState, type MouseEvent } from "react";
import dayjs from "dayjs";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc, type RouterOutputs } from "@/trpc";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { LEAD_NOTE_BODY_MAX_LENGTH } from "../../../../agent/src/core/leadNote/types";

export type LeadNote = RouterOutputs["leadNotes"]["list"][number];

export const noteAuthorLabel = (name: string | undefined, email: string) => name?.trim() || email;

const formatNoteTime = (dateTime: string) => dayjs(dateTime).format("DD/MM/YYYY HH:mm");

const SAVE_ERROR = "No pudimos guardar la nota. Intenta de nuevo.";

const handleError = (fallback: string) => (error: { message?: string }) =>
  toast.error(error.message || fallback);

const LeadNotesSection = ({
  merchantId,
  leadId,
  notes,
}: {
  merchantId: string;
  leadId: string;
  notes: LeadNote[] | undefined;
}) => {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState("");

  const refreshNotes = async () => {
    await Promise.all([
      utils.leadNotes.list.invalidate({ merchantId, leadId }),
      utils.conversations.listConversationTimeline.invalidate({ merchantId, leadId }),
    ]);
  };

  const createNote = trpc.leadNotes.create.useMutation({
    onSuccess: async () => {
      setDraft("");
      toast.success("Nota agregada");
      await refreshNotes();
    },
    onError: handleError(SAVE_ERROR),
  });

  const handleCreate = () => {
    const body = draft.trim();
    if (!body || createNote.isLoading) return;
    createNote.mutate({ merchantId, leadId, body });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Textarea
          id="lead-note-new"
          aria-label="Nueva nota"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          maxLength={LEAD_NOTE_BODY_MAX_LENGTH}
          placeholder="Escribe una nota para tu equipo…"
          disabled={createNote.isLoading}
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-neutral-500">Solo visible para tu equipo</p>
          <Button
            size="sm"
            className="min-h-11 md:min-h-0"
            onClick={handleCreate}
            disabled={!draft.trim() || createNote.isLoading}
          >
            Agregar nota
          </Button>
        </div>
      </div>

      {notes === undefined ? (
        <Skeleton className="h-14 w-full" />
      ) : notes.length === 0 ? (
        <p className="text-sm text-neutral-500">Aún no hay notas para este lead.</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {notes.map((note) => (
            <LeadNoteRow
              key={note.id}
              note={note}
              merchantId={merchantId}
              onChanged={refreshNotes}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

const LeadNoteRow = ({
  note,
  merchantId,
  onChanged,
}: {
  note: LeadNote;
  merchantId: string;
  onChanged: () => Promise<void>;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const updateNote = trpc.leadNotes.update.useMutation({
    onSuccess: async () => {
      setIsEditing(false);
      toast.success("Nota actualizada");
      await onChanged();
    },
    onError: handleError(SAVE_ERROR),
  });

  const deleteNote = trpc.leadNotes.delete.useMutation({
    onSuccess: async () => {
      setIsConfirmOpen(false);
      toast.success("Nota eliminada");
      await onChanged();
    },
    onError: handleError("No pudimos eliminar la nota. Intenta de nuevo."),
  });

  const author = noteAuthorLabel(note.authorName, note.authorEmail);
  const editedLabel = !note.editedAt
    ? ""
    : note.editedByEmail && note.editedByEmail !== note.authorEmail
      ? ` · editada por ${noteAuthorLabel(note.editedByName, note.editedByEmail)}`
      : " · editada";

  const handleStartEdit = () => {
    setDraft(note.body);
    setIsEditing(true);
  };

  const handleSave = () => {
    const body = draft.trim();
    if (!body || updateNote.isLoading) return;
    updateNote.mutate({ merchantId, id: note.id, body });
  };

  const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (deleteNote.isLoading) return;
    deleteNote.mutate({ merchantId, id: note.id });
  };

  return (
    <li className="rounded-md bg-neutral-100/70 px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 pt-1 text-xs text-neutral-600">
          <span className="font-medium text-neutral-800">{author}</span> ·{" "}
          {formatNoteTime(note.createdAt)}
          {editedLabel}
        </p>
        {note.canEdit && !isEditing && (
          <div className="-mr-2 -mt-1 flex shrink-0">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Editar nota"
              className="size-11 md:size-8"
              onClick={handleStartEdit}
            >
              <Pencil />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Eliminar nota"
              className="size-11 md:size-8"
              onClick={() => setIsConfirmOpen(true)}
            >
              <Trash2 />
            </Button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="mt-2 space-y-2">
          <Textarea
            id={`lead-note-edit-${note.id}`}
            aria-label="Editar nota"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={LEAD_NOTE_BODY_MAX_LENGTH}
            disabled={updateNote.isLoading}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11 md:min-h-0"
              onClick={() => setIsEditing(false)}
              disabled={updateNote.isLoading}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="min-h-11 md:min-h-0"
              onClick={handleSave}
              disabled={!draft.trim() || updateNote.isLoading}
            >
              Guardar
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-1 whitespace-pre-wrap text-neutral-900 [overflow-wrap:anywhere]">
          {note.body}
        </p>
      )}

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta nota?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteNote.isLoading}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteNote.isLoading}
              className={buttonVariants({ variant: "destructive" })}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
};

export default LeadNotesSection;
